/**
 * Visual identity of a person when there is NO photo: initials + a stable
 * color.
 *
 * It is the ONLY implementation of these three functions in the monorepo. Before, each
 * screen resolved the initials on its own with `charAt(0)` and different
 * rules (some fell back to the email, others did not; some uppercased, others
 * did not), so the same person looked different depending on where you looked at them.
 *
 * Pure and dependency-free on purpose: it is used by web, desktop, mobile
 * and the tests, and it has to give EXACTLY the same result in all of them.
 */

/** How many initials the fallback avatar carries. */
const MAX_INITIALS = 2;

/** How many color pairs the palette has (see `avatar.tsx`). */
export const AVATAR_PALETTE_SIZE = 8;

/**
 * Initials for the fallback avatar.
 *
 * Takes up to two, from the first and last word of the name. If there is no
 * name it falls back to the email, and if there is none either, to a middle dot (`·`) — never to
 * a question mark, which reads as an error.
 *
 * Uses `codePointAt` and not `[0]`: a name that starts with an emoji or with a
 * character outside the basic plane would split into two illegible halves with the
 * direct index, because JavaScript indexes by UTF-16 unit.
 */
export function initialsOf(name?: string | null, email?: string | null): string {
  const source = (name ?? "").trim() || (email ?? "").trim().split("@")[0] || "";
  const parts = source.split(/[\s._-]+/u).filter(Boolean);
  if (parts.length === 0) {
    return "·";
  }
  const first = parts[0] ?? "";
  const last = parts.length > 1 ? (parts.at(-1) ?? "") : "";
  return [first, last]
    .filter(Boolean)
    .slice(0, MAX_INITIALS)
    .map((part) => String.fromCodePoint(part.codePointAt(0) ?? 0).toLocaleUpperCase())
    .join("");
}

/**
 * Stable color index for a person, in `[0, AVATAR_PALETTE_SIZE)`.
 *
 * Derives from the **userId and not the name**: the name changes (a typo
 * is fixed, the surname is added) and the color must not change with it, or the person
 * "becomes someone else" in the eyes of whoever recognized them by color.
 *
 * 32-bit FNV-1a: cheap, dependency-free and with good dispersion for
 * short strings. It is not cryptographic nor does it pretend to be.
 */
export function avatarPaletteIndex(userId?: string | null): number {
  // NOTE: do not turn this into a default parameter
  // (`userId: string | null = ""`). The pre-commit `oxlint --fix` tries it
  // via `unicorn/prefer-default-parameters`, and it would be a SEMANTIC CHANGE: the
  // default values only apply to `undefined`, so a `null`
  // —which is exactly what arrives from a row with no user— would slip through and
  // blow up on `.length`.
  const key = typeof userId === "string" ? userId : "";

  let hash = 0x81_1c_9d_c5;
  for (let i = 0; i < key.length; i++) {
    // `charCodeAt` and NOT `codePointAt`: we want to walk UTF-16 units one
    // at a time. `codePointAt` returns the COMPLETE code point on
    // surrogate pairs (and `undefined` when past the end), so it would change
    // the hash and, with it, the color of people who already had one assigned. The
    // pre-commit `oxlint --fix` also tries this substitution
    // (`unicorn/prefer-code-point`); here it is wrong.
    // oxlint-disable-next-line unicorn/prefer-code-point
    hash ^= key.charCodeAt(i);
    // `Math.imul` keeps the multiplication in 32 bits; with `*` precision is lost
    // when going past 2^53 and the hash stops being the same on all
    // platforms.
    hash = Math.imul(hash, 0x01_00_01_93);
  }
  return Math.abs(hash) % AVATAR_PALETTE_SIZE;
}

/**
 * Absolute URL of an avatar from the KEY that `user.image` stores.
 *
 * The row stores the key (`avatars/{userId}/{hash}.webp`) and not an absolute URL,
 * so that the row is portable between dev/rc/prod —which have
 * different API origins— and so that a domain change does not force
 * rewriting the database.
 *
 * Tolerates the stored value already being an absolute URL: so a row written
 * by a previous version does not look broken.
 */
export function avatarUrl(key?: string | null, apiBaseUrl?: string | null): string | null {
  const value = (key ?? "").trim();
  if (!value) {
    return null;
  }
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }
  const base = (apiBaseUrl ?? "").replace(/\/+$/u, "");
  return `${base}/api/avatar/${value}`;
}
